<template>
  <div v-if="collection.length">
    <div class="flex w-full items-center justify-between px-24">
      <div class="body-1-bold">
        {{ i18n(MARKET_LIST_TYPES_LABELS[props.type]) }}
      </div>

      <RevButton
        v-if="props.type === MARKET_LIST_TYPES.WITHOUT_BACKBOX"
        :loading="isGettingAllBackboxes"
        size="small"
        variant="primary"
        @click="handleGetAllBackboxes"
      >
        {{ i18n(translations.getAllBackboxes) }}
      </RevButton>
    </div>

    <RevTable :collection :columns transparent-header>
      <template #body-market="{ item }">
        <RevButtonBase
          class="flex items-center gap-8"
          :data-qa="`market-flag-${item.market}`"
          :data-test="`market-flag-link-${item.market}`"
          target="_blank"
          :to="item.productPageLink?.href"
        >
          <Flag :country-code="item.market" size="l" />
          {{ item.market }}
        </RevButtonBase>
      </template>

      <template #body-grades="{ item }">
        <RevTag
          v-if="item.backboxGrade?.label"
          :label="item.backboxGrade.label"
          variant="secondary"
        />
        <span v-else>-</span>
      </template>

      <template #body-minimumPrice="{ item }">
        <RevInputText
          :id="`min-price-${item.market}`"
          v-model="minimumPricesValue[item.market]"
          :error="getMinimumPriceError(item)"
          :label="
            i18n(translations.minimumPriceLabel, {
              currency: i18n.currencySign(item.marketCurrency),
            })
          "
          type="number"
          @keyup.enter="handleEditMarket(item.market)"
        />
      </template>

      <template #body-targetPrice="{ item }">
        <RevInputText
          :id="`price-${item.market}`"
          v-model="targetPricesValue[item.market]"
          :error="getTargetPriceError(item)"
          :label="
            i18n(
              props.hasBackpricerEnabled
                ? translations.targetPriceLabel
                : translations.priceLabel,
              {
                currency: i18n.currencySign(item.marketCurrency),
              },
            )
          "
          type="number"
          @keyup.enter="handleEditMarket(item.market)"
        />
      </template>

      <template #body-alerts="{ item }">
        <PricingStrategy
          v-if="shouldShowPricingStrategy(item)"
          :listing="props.listing"
          :market="item"
          :pricing-strategy="item.pricingStrategy!"
        />
        <Alert v-else :item :listing-id="props.listing.id" :type />
      </template>

      <template #body-actions="{ item }">
        <div class="flex items-center space-x-8">
          <RevButton
            :disabled="!canSaveMarket(item.market)"
            :loading="isSaving"
            size="small"
            variant="secondary"
            @click="handleEditMarket(item.market)"
          >
            {{ i18n(translations.save) }}
          </RevButton>

          <RevButton
            v-if="type === MARKET_LIST_TYPES.WITHOUT_BACKBOX"
            :loading="isGettingBackbox"
            size="small"
            variant="secondary"
            @click="handleGetBackbox(item.market)"
          >
            {{ i18n(translations.getBackbox) }}
          </RevButton>
        </div>
      </template>
    </RevTable>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref, watch } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  Listing,
  MarketListing,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevInputText } from '@ds/components/InputText'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'
import { useListings } from '~/scopes/listings/pages/index/composables/useListings'

import {
  MARKET_LIST_TYPES,
  MARKET_LIST_TYPES_LABELS,
  PRICE_IS_TO_LOW_RULE,
} from './MarketList.constants'
import translations from './MarketList.translations'
import Alert from './components/Alert.vue'
import PricingStrategy from './components/PricingStrategy.vue'

const i18n = useI18n()
const { allMarkets } = useMarketplace()
const { openErrorToast } = useTheToast()
const {
  updateListing,
  getBackboxForMarket,
  getAllBackboxesForListing,
  shouldShowPricingStrategy,
  formatListingToTrackingEvent,
} = useListings()
const { isFeatureEnabled } = useBusinessFeaturesStore()

const tracking = useTracking()

const props = defineProps<{
  type: string
  hasBackpricerEnabled: boolean
  listing: Listing
}>()

const isDynamicPricingUpEnabled = isFeatureEnabled(
  'isDynamicPricingUpFirstIterationEnabled',
)

const hasBackbox = computed(() => props.type === MARKET_LIST_TYPES.WITH_BACKBOX)

const marketsToDisplay = computed(() => {
  if (props.type === MARKET_LIST_TYPES.WITH_BACKBOX) {
    return props.listing.markets.withBackbox
  }
  if (props.type === MARKET_LIST_TYPES.WITHOUT_BACKBOX) {
    return props.listing.markets.withoutBackbox
  }
  if (props.type === MARKET_LIST_TYPES.OFFLINE) {
    return props.listing.markets.offline
  }

  return {} as Record<MarketCountryCode, MarketListing>
})

const columns = [
  { key: 'market', label: i18n(translations.market) },
  { key: 'grades', label: i18n(translations.grades) },
  ...insertIf(hasBackbox.value, [
    { key: 'currentBackboxPriceLabel', label: i18n(translations.price) },
  ]),
  ...insertIf(!hasBackbox.value, [
    {
      key: 'backboxPriceLabel',
      label: i18n(translations.backboxPrice),
      ...insertIf(isDynamicPricingUpEnabled, {
        description: i18n(translations.backboxPriceTooltip),
      }),
    },
    {
      key: 'currentBMPriceLabel',
      label: i18n(translations.currentBMPrice),
    },
  ]),
  ...insertIf(props.hasBackpricerEnabled, [
    { key: 'minimumPrice', label: i18n(translations.minimumPrice) },
  ]),
  {
    key: 'targetPrice',
    label: i18n(
      props.hasBackpricerEnabled
        ? translations.targetPrice
        : translations.price,
    ),
  },
  {
    key: 'alerts',
    label:
      isFeatureEnabled('isSalesMaximizerPriceEnabled') &&
      props.type !== MARKET_LIST_TYPES.OFFLINE
        ? i18n(translations.pricingStrategy)
        : i18n(translations.alerts),
  },
  { key: 'actions', label: '' },
]

const collection = computed(() =>
  Object.entries(marketsToDisplay.value).map(([countryCode, market]) => ({
    market: countryCode as MarketCountryCode,
    marketCurrency:
      allMarkets[countryCode as MarketCountryCode].defaultCurrency,
    ...market,
    currentBackboxPriceLabel: market.currentBackboxPrice
      ? i18n.price(market.currentBackboxPrice)
      : '-',
    backboxPriceLabel: market.backboxPrice
      ? i18n.price(market.backboxPrice)
      : '-',
    currentBMPriceLabel: market.currentBackboxPrice
      ? i18n.price(market.currentBackboxPrice)
      : '-',
  })),
)

const minimumPricesValue = ref<Partial<Record<MarketCountryCode, number>>>({})
const targetPricesValue = ref<Partial<Record<MarketCountryCode, number>>>({})
const isGettingBackbox = ref(false)
const isGettingAllBackboxes = ref(false)
const isSaving = ref(false)

function minimumPriceIsBelowTargetPrice(market: MarketCountryCode) {
  const minPrice = Number(minimumPricesValue.value[market])
  const targetPrice = Number(targetPricesValue.value[market])

  return minPrice && targetPrice && minPrice > targetPrice
}

function targetPriceIsTooHigh(market: MarketCountryCode) {
  const minimumPrice = Number(minimumPricesValue.value[market])
  const targetPrice = Number(targetPricesValue.value[market])

  if (!minimumPrice || !targetPrice) {
    return false
  }

  const maximumTargetPrice =
    minimumPrice * PRICE_IS_TO_LOW_RULE.MAXIMUM_TARGET_PRICE_MULTIPLIER

  return targetPrice > maximumTargetPrice
}

const canSaveMarket = computed(() => (market: MarketCountryCode) => {
  const currentMinimumPriceForMarket =
    Number(marketsToDisplay.value[market].minimumPrice?.amount) || undefined
  const currentTargetPriceForMarket =
    Number(marketsToDisplay.value[market].price?.amount) || undefined

  if (
    !targetPricesValue.value[market] ||
    Number(targetPricesValue.value[market]) === 0
  ) {
    return false
  }

  return (
    (minimumPricesValue.value[market] !== currentMinimumPriceForMarket ||
      targetPricesValue.value[market] !== currentTargetPriceForMarket) &&
    !targetPriceIsTooHigh(market) &&
    !minimumPriceIsBelowTargetPrice(market)
  )
})

function getMinimumPriceError(
  item: MarketListing & { market: MarketCountryCode },
) {
  if (minimumPriceIsBelowTargetPrice(item.market)) {
    return i18n(translations.minimumPriceBelowTargetPriceError)
  }

  return undefined
}

function getTargetPriceError(
  item: MarketListing & { market: MarketCountryCode },
) {
  if (targetPriceIsTooHigh(item.market)) {
    return i18n(translations.targetPriceIsTooHigh, {
      limit: Math.round(
        PRICE_IS_TO_LOW_RULE.MAXIMUM_TARGET_PRICE_MULTIPLIER * 100 - 100,
      ),
    })
  }

  return undefined
}

function initializeInputValues() {
  Object.entries(marketsToDisplay.value).forEach(([countryCode, market]) => {
    minimumPricesValue.value[countryCode as MarketCountryCode] =
      Number(market.minimumPrice?.amount) || undefined

    targetPricesValue.value[countryCode as MarketCountryCode] =
      Number(market.price?.amount) || undefined
  })
}

async function handleEditMarket(market: MarketCountryCode) {
  if (canSaveMarket.value(market)) {
    try {
      isSaving.value = true

      tracking.trackClick({
        page_type: 'listings',
        zone: 'listing_price_update',
        name: 'save',
        ...formatListingToTrackingEvent(props.listing, {
          ...marketsToDisplay.value[market],
          market,
          minimumPrice: minimumPricesValue.value[market]
            ? ({
                amount: minimumPricesValue.value[market]?.toString(),
                currency: allMarkets[market].defaultCurrency,
              } as MonetaryAmount)
            : null,
          price: targetPricesValue.value[market]
            ? ({
                amount: targetPricesValue.value[market]?.toString(),
                currency: allMarkets[market].defaultCurrency,
              } as MonetaryAmount)
            : null,
        }),
      })

      await updateListing(props.listing.id, {
        markets: {
          [market]: {
            ...insertIf(Boolean(targetPricesValue.value[market]), {
              price: {
                amount: targetPricesValue.value[market]?.toString(),
                currency: allMarkets[market].defaultCurrency,
              },
            }),
            ...insertIf(props.hasBackpricerEnabled, {
              minimumPrice: minimumPricesValue.value[market]
                ? {
                    amount: minimumPricesValue.value[market]?.toString(),
                    currency: allMarkets[market].defaultCurrency,
                  }
                : null,
            }),
          },
        },
      })
    } catch (error) {
      openErrorToast({
        content: getApiErrorMessage(error as unknown as Error),
      })
    } finally {
      isSaving.value = false
    }
  }
}

async function handleGetBackbox(market: MarketCountryCode) {
  isGettingBackbox.value = true

  try {
    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_price_update',
      name: 'win-backbox',
      ...formatListingToTrackingEvent(props.listing, {
        ...marketsToDisplay.value[market],
        market,
      }),
    })

    await getBackboxForMarket(props.listing.id, market)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isGettingBackbox.value = false
  }
}

async function handleGetAllBackboxes() {
  isGettingAllBackboxes.value = true

  try {
    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_price_update',
      name: 'win-all-backbox',
      ...formatListingToTrackingEvent(props.listing),
    })
    await getAllBackboxesForListing(props.listing.id)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isGettingAllBackboxes.value = false
  }
}

watch(
  props,
  () => {
    initializeInputValues()
  },
  { immediate: true },
)
</script>
